/**
 * COMMERCE-WORKSPACE-FRAMEWORK-000001 — deep links + peer routes.
 * Marketplace-agnostic path map for Amazon MarketSync founder desks today.
 */
import type { CommerceDeepLinkDomain, CommerceDomain, CommercePeerId } from './types';

export const COMMERCE_DOMAIN_PATHS: Record<string, string> = {
  catalog: '/amazon/catalog',
  inventory: '/amazon/inventory',
  orders: '/amazon/orders',
  order_items: '/amazon/order-items',
  pricing: '/amazon/pricing',
  finance: '/amazon/finance',
  shipments: '/amazon/shipments',
  notifications: '/amazon/notifications',
  connect: '/amazon/connect',
};

export const DEFAULT_COMMERCE_PEERS: {
  id: CommercePeerId;
  label: string;
  path: string;
}[] = [
  { id: 'catalog', label: 'Products', path: COMMERCE_DOMAIN_PATHS.catalog },
  { id: 'inventory', label: 'Inventory', path: COMMERCE_DOMAIN_PATHS.inventory },
  { id: 'orders', label: 'Orders', path: COMMERCE_DOMAIN_PATHS.orders },
  { id: 'order_items', label: 'Order Items', path: COMMERCE_DOMAIN_PATHS.order_items },
  { id: 'pricing', label: 'Pricing', path: COMMERCE_DOMAIN_PATHS.pricing },
  { id: 'finance', label: 'Finance', path: COMMERCE_DOMAIN_PATHS.finance },
  { id: 'connect', label: 'Connect', path: COMMERCE_DOMAIN_PATHS.connect },
];

export function commercePath(domain: CommerceDeepLinkDomain | 'connect'): string {
  return COMMERCE_DOMAIN_PATHS[domain] ?? '/amazon/notifications';
}

export function commerceDeepLink(
  domain: CommerceDeepLinkDomain,
  params: {
    connection?: string;
    q?: string;
    asin?: string;
    order_id?: string;
    /** Optional GFE query fragment (without ?) to merge for universal context. */
    gfeQuery?: string;
  },
): string {
  const base = commercePath(domain);
  const sp = new URLSearchParams(params.gfeQuery || '');
  if (params.connection) sp.set('connection', params.connection);
  if (params.q) sp.set('q', params.q);
  if (params.asin) sp.set('asin', params.asin);
  if (params.order_id) sp.set('order_id', params.order_id);
  const qs = sp.toString();
  return qs ? `${base}?${qs}` : base;
}

export function peersForDomain(
  domain: CommerceDomain,
  allow?: CommercePeerId[],
): typeof DEFAULT_COMMERCE_PEERS {
  if (!allow || allow.length === 0) return DEFAULT_COMMERCE_PEERS;
  const set = new Set(allow);
  return DEFAULT_COMMERCE_PEERS.filter((p) => set.has(p.id) || p.id === domain || p.id === 'connect');
}
